import { FC, useEffect, useState } from 'react'
import { useSendPasswordResetEmail } from 'react-firebase-hooks/auth'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Dialog, DialogTitle, Stack } from '@mui/material'
import { multiFactor } from 'firebase/auth'

import { LocalStorageKey, setLocalStorageValue } from '@/common/utils/localStorage'
import { fbAuth, signOut } from '@/modules/authentication'
import { useCurrentUser } from '@/modules/user/hooks'

const ProfileModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [sendPasswordResetEmail, resetSending, resetError] = useSendPasswordResetEmail(fbAuth)
  const [passwordResetSent, setPasswordResetSent] = useState(false)
  const { data: user } = useCurrentUser()
  const { t } = useTranslation()
  const [enrolledMfa, setEnrolledMfa] = useState(false)

  useEffect(() => {
    if (fbAuth.currentUser) {
      const multiFactorUser = multiFactor(fbAuth.currentUser)
      if (multiFactorUser.enrolledFactors.length > 0) setEnrolledMfa(true)
    }
  }, [fbAuth])

  const resetPassword = async () => {
    await sendPasswordResetEmail(user!.email)
    setPasswordResetSent(true)
  }

  const removeMfa = () => {
    setLocalStorageValue(LocalStorageKey.UNENROLL_MFA, 'true')
    signOut()
  }

  const addMfa = () => {
    setLocalStorageValue(LocalStorageKey.ENROLL_MFA, 'true')
    signOut()
  }

  return (
    <Dialog className="edit-profile-modal" open={true} onClose={onClose}>
      <DialogTitle>{t('profileModal.profileSettings', 'Profile Settings')}</DialogTitle>
      <Stack spacing={2} sx={{ padding: '16px' }}>
        <Button variant="contained" onClick={resetPassword}>
          {t('profileModal.resetPassword', 'Reset Password')}
        </Button>
        {enrolledMfa ? (
          <Button onClick={removeMfa}>
            {t('profileModal.removeMfa', 'Remove multi-factor device')}
          </Button>
        ) : (
          <Button variant="contained" onClick={addMfa}>
            {t('profileModal.addMfa', 'Add multi-factor device')}
          </Button>
        )}
        {passwordResetSent && !resetSending && !resetError && (
          <Alert severity="success">
            {t(
              'profileModal.resetEmailMessage',
              'An email with a link to reset your password has been sent to {{user_email}}.',
              { user_email: user?.email || 'your email' }
            )}
          </Alert>
        )}
      </Stack>
    </Dialog>
  )
}

export default ProfileModal
